import { ChildRef } from '../../../../../lib/web/components/child-ref';
import { ChildrenRef } from '../../../../../lib/web/components/children-ref';
import { ClickListener } from '../../../../../lib/web/components/click-listener';
import { Component } from '../../../../../lib/web/components/component';
import { ComponentBase } from '../../../../../lib/web/components/component-base';
import { EventListener } from '../../../../../lib/web/components/event-listener';
import { Input } from '../../../../../lib/web/components/input';
import { Str } from '../../../../../lib/web/core/str';
import { DataEventService } from '../../../services/data-event.service';
import { SessionService } from '../../../services/session.service';
import { UserFavoriteService } from '../../../services/user-favorite.service';
import { IconComponent } from '../../atoms/icon/icon.component';
import { TabComponent } from '../../atoms/tab/tab.component';
import { ViewDetailComponent } from '../../molecules/view-detail/view-detail.component';

@Component({
    selector: '.p-partner-landing'
})
export class PartnerLandingComponent extends ComponentBase<HTMLElement> {

    private _scrollingToSection: boolean = false;
    private _scrollEndTimeout: number = null;
    private _scrollSpyFrame: number = null;
    private _scrollLoaded: boolean = false;
    private _pendingSectionId: string = null;

    @Input('id')
    private _id: number = null;

    @ChildRef('.a-icon--add')
    private _favoriteIcon: IconComponent = null;
    @ChildRef('.a-tab')
    private _tab: TabComponent = null;

    public constructor(node: HTMLElement, 
        private _sessionService: SessionService,
        private _userFavoriteService: UserFavoriteService,
        private _dataEventService: DataEventService) {
        super(node);

        this.addWindowEventListener('user-favorites-changed', () => {
            if (this._sessionService.isSigned) {
                this.setFavoriteOption();
            }
        });
        this.addWindowEventListener('resize', () => this.checkOptionsTooltip());
        this.addWindowEventListener('scroll', () => this.onWindowScroll());
        this.addWindowEventListener('scroll-loaded', () => this.onScrollLoaded());
    }

    public onInit(): void {
        super.onInit();
        if (this._sessionService.isSigned) {
            this.setFavoriteOption();
        }
        this.checkOptionsTooltip();

        const queryTabId: string = new URLSearchParams(location.search).get('tab');
        let tabId: string = queryTabId;
        if (!tabId && this._tab.node.querySelectorAll('.a-tab__item').length > 1) {
            tabId = Array.from(this._tab.node.querySelectorAll<HTMLButtonElement>('.a-tab__item'))[1].dataset.id;
        }
        if (tabId) {
            if (this.anchorMode) {
                // Solo se hace scroll si la sección viene del querystring y no es la primera.
                // Se espera a 'scroll-loaded' porque ScrollService lleva la página arriba tras el bootstrap
                const id: string = Str.slugToCamel(tabId);
                this.setSelectedAnchorTab(id, null);
                if (queryTabId && this.sections[0]?.dataset.id !== id) {
                    this._pendingSectionId = id;
                }
            }
            else {
                this._tab.setSelected(Str.slugToCamel(tabId));
            }
        }
    }    

    private onScrollLoaded(): void {
        this._scrollLoaded = true;
        if (this._pendingSectionId) {
            this.setSelectedAnchorTab(this._pendingSectionId, 'auto');
            this._pendingSectionId = null;
        }
    }

    private get anchorMode(): boolean {
        return this._node.querySelector<HTMLElement>('.p-partner-landing__content')?.dataset.anchorMode === 'true';
    }

    private get sections(): HTMLElement[] {
        return Array.from(this._node.querySelectorAll<HTMLElement>('.p-partner-landing__section'));
    }

    @ClickListener('.p-partner-landing__toggle-favorite')
    public onToogleFavorite(): void {
        this.setFavorite();
    }

    @EventListener('selection-changed', '.a-tab')
    public onTabSelectionChanged(e: CustomEvent): void {
        if (this.anchorMode) {
            this.setSelectedAnchorTab(e.detail.id, 'smooth');
        }
        else {
            this.setSelectedTab(e.detail.id);
        }
    }

    private setSelectedAnchorTab(id: string, scrollBehavior: ScrollBehavior): void {
        const isDetails: boolean = id === 'details';
        const section: HTMLElement = this.sections.find((s: HTMLElement) => s.dataset.id === id);
        if (!isDetails && !section) {
            return;
        }
        this._tab.setSelected(id);
        this.addOrRemoveClass(!isDetails, 'u-hidden', '.p-partner-landing__tab-details');
        this.addOrRemoveClass(isDetails, 'u-hidden', '.p-partner-landing__tab-sections');
        this.replaceState(`${location.pathname}?tab=${Str.camelToKebab(id)}`);
        if (section && scrollBehavior) {
            this._scrollingToSection = true;
            this.resetScrollEndTimeout();
            section.scrollIntoView({ behavior: scrollBehavior, block: 'start' });
        }
    }

    private onWindowScroll(): void {
        if (this._scrollingToSection) {
            // Mientras dura el scroll programático no se recalcula la pestaña activa
            this.resetScrollEndTimeout();
            return;
        }
        if (this.anchorMode && this._scrollLoaded && !this._scrollSpyFrame) {
            this._scrollSpyFrame = window.requestAnimationFrame(() => {
                this._scrollSpyFrame = null;
                this.updateSelectedTabFromScroll();
            });
        }
    }

    private resetScrollEndTimeout(): void {
        window.clearTimeout(this._scrollEndTimeout);
        this._scrollEndTimeout = window.setTimeout(() => this._scrollingToSection = false, 150);
    }

    private updateSelectedTabFromScroll(): void {
        const sections: HTMLElement[] = this.sections;
        if (!sections.length || this._tab.node.dataset.selected === 'details') {
            return;
        }
        const threshold: number = window.innerHeight / 3;
        const isAtBottom: boolean = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 1;
        const current: HTMLElement = isAtBottom
            ? sections[sections.length - 1]
            : sections.filter((s: HTMLElement) => s.getBoundingClientRect().top <= threshold).pop() || sections[0];
        if (current.dataset.id !== this._tab.node.dataset.selected) {
            this._tab.setSelected(current.dataset.id);
            this.replaceState(`${location.pathname}?tab=${Str.camelToKebab(current.dataset.id)}`);
        }
    }

    private setSelectedTab(id: string): void {
        const tabs: NodeListOf<HTMLDivElement> = this._node.querySelectorAll<HTMLDivElement>('.p-partner-landing__tab');
        tabs.forEach((tab: HTMLDivElement) => {
            tab.classList.add('u-hidden');
        });
        Array.from(tabs).find((tab: HTMLDivElement) => tab.dataset.id === id)?.classList.remove('u-hidden');
        this.replaceState(`${location.pathname}?tab=${Str.camelToKebab(id)}`);    
    }

    private checkOptionsTooltip(): void {
        const buttons: HTMLButtonElement[] = Array.from(this._node.querySelectorAll<HTMLButtonElement>('.p-partner-landing__options button'));
        buttons.forEach(button => {
            const textElement: HTMLElement = button.querySelector(':scope > span');
            if (textElement) {
                button.dataset.tooltipEnabled = getComputedStyle(textElement).display === 'none' ? 'true': 'false';
            }
        });
    }

    private async setFavorite(): Promise<void> {
        if (this._userFavoriteService.isFavorite(this._id, 'partner-landing')) {
            if (await this.showQuestion(USER_LOCALE.removeFromFavorite, USER_LOCALE.removeFromFavoriteQuestion.format('<br><br>'), 'ok', { result: 'cancel' }, { result: 'ok' }) == 'ok') {
                await this._userFavoriteService.remove(this._id, 'partner-landing');
                this._dataEventService.favorite(false, new URL(location.href).pathname);
            }
        }
        else {
            await this._userFavoriteService.add(this._id, 'partner-landing');
            this._dataEventService.addFavorite('partner_landing', this._node.dataset.id, this._node.dataset.display, null, null);
            this._dataEventService.favorite(true, new URL(location.href).pathname);
        }
    }

    private setFavoriteOption(): void {
        const isFavorite: boolean = this._userFavoriteService.isFavorite(this._id, 'partner-landing');
        this.removeClass('u-hidden-important', '.p-partner-landing__toggle-favorite');
        // this.setInnerHTML(isFavorite ? USER_LOCALE.removeFromFavorite: USER_LOCALE.addToFavorite, '.p-partner-landing__toggle-favorite span:last-child');
        this._favoriteIcon.key = isFavorite ? 'remove': 'add';
    }

    public dispose(): void {
        window.clearTimeout(this._scrollEndTimeout);
        window.cancelAnimationFrame(this._scrollSpyFrame);
        super.dispose();
    }
}